import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Space, Typography, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { EpcApprovedRevisionPicker } from './EpcApprovedRevisionPicker';

const { Text, Paragraph } = Typography;

export interface EpcPrepareProposalFromEstimateModalProps {
  open: boolean;
  onClose: () => void;
  tenderId: string;
  estimateId: string;
  currency: string;
  revisions: Array<Record<string, unknown>>;
  existingProposalId?: string | null;
  onCertified?: (proposalId: string) => void;
}

/**
 * One-click: create/link EPC commercial proposal + certify Approved EST3 revision.
 * KEEP_SEPARATE from Manufacturing Sales Quotation.
 */
export const EpcPrepareProposalFromEstimateModal: React.FC<
  EpcPrepareProposalFromEstimateModalProps
> = ({
  open,
  onClose,
  tenderId,
  estimateId,
  currency,
  revisions,
  existingProposalId,
  onCertified,
}) => {
  const qc = useQueryClient();
  const approved = useMemo(
    () =>
      (revisions || []).filter((r) => String(r.status || '').toLowerCase() === 'approved'),
    [revisions]
  );
  const [revisionId, setRevisionId] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    const first = approved[0];
    setRevisionId(first ? String(first.id) : undefined);
  }, [open, approved]);

  const runM = useMutation({
    mutationFn: async () => {
      if (!revisionId) throw new Error('Select an Approved revision');
      let proposalId = existingProposalId || '';
      if (!proposalId) {
        const created = await epcCommercialService.createProposal({
          tenderId,
          currency,
          estimationId: estimateId,
        });
        proposalId = String(created.id);
      } else {
        // Ensure estimation_id is linked when proposal already exists.
        await epcCommercialService.patchProposal(proposalId, { estimationId: estimateId });
      }
      await epcCommercialService.certifyProposalEstimateBasis(proposalId, {
        estimate_id: estimateId,
        estimate_revision_id: revisionId,
        replace: true,
      });
      return proposalId;
    },
    onSuccess: (proposalId) => {
      message.success('Proposal prepared and estimate basis certified');
      void qc.invalidateQueries({ queryKey: ['epc-proposals', tenderId] });
      void qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      void qc.invalidateQueries({ queryKey: ['epc-proposal-submission-basis', proposalId] });
      onCertified?.(proposalId);
      onClose();
    },
    onError: (e: Error) => message.error(e.message || 'Prepare proposal failed'),
  });

  return (
    <Modal
      title="Prepare proposal from estimate"
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={runM.isPending}
            disabled={!revisionId || approved.length === 0}
            onClick={() => runM.mutate()}
          >
            {existingProposalId ? 'Certify onto proposal' : 'Create proposal & certify'}
          </Button>
        </Space>
      }
    >
      <Paragraph type="secondary">
        Pins an <Text strong>Approved</Text> EST3 revision onto an EPC commercial proposal (Final Bid
        becomes commercial value). Does not create a Manufacturing Sales Quotation.
      </Paragraph>
      {approved.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="No Approved revision"
          description="Submit and approve an estimate revision before preparing the proposal."
        />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {existingProposalId ? (
            <Alert type="info" showIcon message="Will certify onto the existing tender proposal" />
          ) : (
            <Alert type="info" showIcon message="Will create a new Draft commercial proposal" />
          )}
          <EpcApprovedRevisionPicker
            revisions={revisions}
            value={revisionId}
            onChange={setRevisionId}
          />
        </Space>
      )}
    </Modal>
  );
};

export default EpcPrepareProposalFromEstimateModal;
